import { useState, useEffect } from 'react';
import { PrimaryButton, SecondaryButton, Typography } from '@weareopenr/component-library';
import { IMultistepWizardProps } from './types';
import { useWizardState } from './useWizardState';
import { WizardProvider } from './WizardContext';
import classes from 'classnames/bind';
import styles from './MultistepWizard.module.scss';

const cx = classes.bind(styles);

/**
 * A flexible multistep wizard component for creating guided flows with validation.
 *
 * Features:
 * - Multi-step navigation with Previous/Next/Cancel buttons
 * - Step validation before allowing navigation
 * - Loading states during submission
 * - Progress indicator showing current step
 * - Automatic data management across steps
 * - Support for optional steps that can be skipped
 *
 * @template TData - The type of data managed by the wizard
 *
 * Props:
 * - steps: Array of wizard steps to display
 * - initialData: Initial data to populate the wizard
 * - onComplete: Callback executed when wizard completes (last step)
 * - onCancel: Callback executed when Cancel button is clicked
 * - isSubmitting: Shows loading state on Complete button (default: false)
 * - footer: Custom footer to replace default navigation buttons
 * - showProgress: Display "Step X of Y" indicator (default: true)
 * - className: Additional CSS class for the wizard container
 * - validateOnNavigation: Validate steps before allowing navigation (default: false)
 *
 * @example
 * ```tsx
 * const steps = [
 *   {
 *     id: 'user-details',
 *     title: 'User Details',
 *     subtitle: 'Enter user information',
 *     component: <UserDetailsStep />,
 *     validate: (data) => !!data.email && !!data.name
 *   }
 * ];
 *
 * <MultistepWizard
 *   steps={steps}
 *   initialData={{ email: '', name: '' }}
 *   onComplete={(data) => console.log(data)}
 *   onCancel={() => console.log('cancelled')}
 *   validateOnNavigation={true}
 * />
 * ```
 */
export const MultistepWizard = <TData,>({
  steps,
  initialData,
  onComplete,
  onCancel,
  isSubmitting = false,
  footer,
  showProgress = true,
  className,
  validateOnNavigation = false,
}: IMultistepWizardProps<TData>) => {
  const { currentStep, navigation, data, validateStep } = useWizardState({
    steps,
    initialData,
    validateOnNavigation,
  });

  const [isCurrentStepValid, setIsCurrentStepValid] = useState(!validateOnNavigation);
  const isLastStep = navigation.currentStep === navigation.totalSteps - 1;

  // Check if current step is valid when validation is enabled
  useEffect(() => {
    if (!validateOnNavigation || !currentStep) {
      setIsCurrentStepValid(true);
      return;
    }

    const checkValidation = async () => {
      const isValid = await validateStep(navigation.currentStep);
      setIsCurrentStepValid(isValid);
    };

    checkValidation();
  }, [validateOnNavigation, currentStep, navigation.currentStep, validateStep, data.data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLastStep) {
      // Validate current step before completing
      if (validateOnNavigation) {
        const isValid = await validateStep(navigation.currentStep);
        if (!isValid) return;
      }
      await onComplete(data.data);
    } else {
      navigation.goNext();
    }
  };

  const handlePrevious = () => {
    navigation.goPrevious();
  };

  const handleSkip = () => {
    navigation.skipStep();
  };

  const wizardContextValue = {
    navigation,
    data,
    isSubmitting,
    currentStep,
  };

  return (
    <WizardProvider value={wizardContextValue}>
      <form className={cx('multistep-wizard', className)} onSubmit={handleSubmit}>
        <div className={cx('wizard-header')}>
          {showProgress && navigation.totalSteps > 1 && (
            <Typography
              colour="primary"
              size="medium"
              weight="semi-bold"
              className={cx('wizard-progress-text')}
            >
              {`Step ${navigation.currentStep + 1} of ${navigation.totalSteps}`}
            </Typography>
          )}
          {currentStep && (
            <>
              <Typography size="xxlarge" weight="bold">
                {currentStep.title}
              </Typography>
              <Typography size="medium" weight="regular">
                {currentStep.subtitle}
              </Typography>
            </>
          )}
        </div>

        <div className={cx('wizard-content')}>{currentStep?.component}</div>

        {footer || (
          <div className={cx('wizard-footer')}>
            <div className={cx('left-buttons')}>
              <SecondaryButton
                type="button"
                size="full-width"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </SecondaryButton>
              {navigation.canPrevious && (
                <SecondaryButton
                  type="button"
                  size="full-width"
                  onClick={handlePrevious}
                  disabled={isSubmitting}
                >
                  Previous
                </SecondaryButton>
              )}
            </div>
            <div className={cx('right-buttons')}>
              {currentStep?.optional && !isLastStep && (
                <PrimaryButton
                  type="button"
                  size="full-width"
                  onClick={handleSkip}
                  disabled={isSubmitting}
                >
                  Skip
                </PrimaryButton>
              )}
              <PrimaryButton
                type="submit"
                size="full-width"
                disabled={isSubmitting || (validateOnNavigation && !isCurrentStepValid)}
                loading={isSubmitting && isLastStep}
              >
                {isLastStep ? 'Complete' : 'Next'}
              </PrimaryButton>
            </div>
          </div>
        )}
      </form>
    </WizardProvider>
  );
};
